import { type ReactNode, useLayoutEffect, useRef, useState } from "react";
import { Button } from "../ui/button";

/** Blocks taller than this collapse behind "Show more". */
export const SHOW_MORE_MAX_HEIGHT = 320;

/**
 * Clips a tall block and reveals the rest on demand. A mask fades the last
 * lines into the surface behind them, so it works on the page and inside a card.
 */
export function ShowMore({
  children,
  maxHeight = SHOW_MORE_MAX_HEIGHT,
  expandOnFocus = false,
}: {
  children: ReactNode;
  maxHeight?: number;
  /** Opens the block when it receives focus, so an editor is not edited while clipped. */
  expandOnFocus?: boolean;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [overflows, setOverflows] = useState(false);
  const [expanded, setExpanded] = useState(false);

  useLayoutEffect(() => {
    const node = ref.current;
    if (!node) return;
    const measure = () => setOverflows(node.scrollHeight > maxHeight + 40);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    // focusin and input bubble out of the editor, which is not a form field of this div.
    const open = () => setExpanded(true);
    if (expandOnFocus) {
      node.addEventListener("focusin", open);
      node.addEventListener("input", open);
    }
    return () => {
      observer.disconnect();
      node.removeEventListener("focusin", open);
      node.removeEventListener("input", open);
    };
  }, [maxHeight, expandOnFocus]);

  const clamped = overflows && !expanded;
  return (
    <div>
      <div
        ref={ref}
        className={clamped ? "overflow-hidden" : undefined}
        style={
          clamped
            ? {
                maxHeight,
                maskImage: "linear-gradient(to bottom, black calc(100% - 3rem), transparent)",
                WebkitMaskImage: "linear-gradient(to bottom, black calc(100% - 3rem), transparent)",
              }
            : undefined
        }
      >
        {children}
      </div>
      {overflows && (
        <Button
          variant="link"
          size="sm"
          className="h-auto px-0 pt-1 text-xs"
          aria-expanded={expanded}
          onClick={() => setExpanded((open) => !open)}
        >
          {expanded ? "Show less" : "Show more"}
        </Button>
      )}
    </div>
  );
}
